import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

// const baseURL = "http://192.168.0.11:3001";
const baseURL = "https://connect.dhamaka.app"

export default function MultiOTTRazorpay() {
    const hasCalled = useRef(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [calculationData, setCalculationData] = useState(null);
    const [paymentSuccessData, setPaymentSuccessData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [countdown, setCountdown] = useState(4);

    // Helper function to load the Razorpay SDK script dynamically
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handleContinue = useCallback((trxId) => {
        const id = trxId || paymentSuccessData?.transactionId;
        if (id && id !== "N/A") {
            window.location.href = `multiott://payment-success?transaction_id=${id}`;
        }
    }, [paymentSuccessData]);

    // Automatic redirection countdown after payment success
    useEffect(() => {
        if (paymentSuccessData?.transactionId) {
            setCountdown(4);
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleContinue(paymentSuccessData.transactionId);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [paymentSuccessData, handleContinue]);

    const initiatePayment = useCallback(async () => {
        const params = new URL(window.location.href).searchParams;
        const userId = params.get("userId") || params.get("user_id");
        const ottId = params.get("ottId") || params.get("ott_id");
        const planId = params.get("planId") || params.get("plan_id");
        const movieId = params.get("movieId") || params.get("movie_id");
        const seriesId = params.get("seriesId") || params.get("series_id");
        const isAdFreeParam = params.get("isAdFree") || params.get("is_ad_free");
        const isAdFree = isAdFreeParam === "true" || isAdFreeParam === true;
        const couponCode = params.get("couponCode") || params.get("coupon_code");

        console.log("Parsed URL Parameters:", { userId, ottId, planId, movieId, seriesId, isAdFree, couponCode });

        if (!userId || !ottId || (!planId && !movieId && !seriesId)) {
            const missing = [];
            if (!ottId) missing.push("ott_id");
            if (!userId) missing.push("user_id");
            if (!planId && !movieId && !seriesId) missing.push("plan_id / movie_id / series_id");
            const msg = `Missing required query parameter(s): ${missing.join(", ")}`;
            setError(msg);
            console.error(msg);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // 1. Load Razorpay script
            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                throw new Error("Razorpay SDK failed to load. Please check your internet connection.");
            }

            // 2. Call calculate-amount API (Supports SVOD Plan & TVOD Movie/Series Rental)
            const queryParams = {
                ottId: Number(ottId),
                userId: Number(userId),
            };

            if (planId) {
                queryParams.planId = Number(planId);
            }
            if (movieId) {
                queryParams.movieId = Number(movieId);
                queryParams.isAdFree = isAdFree;
            }
            if (seriesId) {
                queryParams.seriesId = Number(seriesId);
                queryParams.isAdFree = isAdFree;
            }
            if (couponCode) {
                queryParams.couponCode = couponCode;
            }

            console.log("Calling calculate-amount API with query params:", queryParams);

            const response = await axios.get(`${baseURL}/api/v1/payment/calculate-amount`, {
                params: queryParams,
                headers: {
                    accept: '*/*'
                }
            });

            console.log("Calculate Amount API Response:", response.data);

            const resultData = response.data?.data;
            if (!resultData || resultData.amountToBePaid === undefined || resultData.amountToBePaid === null) {
                throw new Error("Invalid response from calculate-amount API: 'amountToBePaid' is missing.");
            }

            setCalculationData(resultData);

            const payableAmount = Number(resultData.amountToBePaid);
            console.log(`Amount to be paid: ₹${payableAmount} (${payableAmount * 100} paise)`);

            // Determine description for Razorpay checkout
            let orderDescription = "Order Details";
            if (resultData?.plan?.planName) {
                orderDescription = `${resultData.plan.planName} Subscription`;
            } else if (resultData?.movie?.title || resultData?.movie?.name) {
                orderDescription = `Movie: ${resultData.movie.title || resultData.movie.name}${isAdFree ? " (Ad-Free)" : " (With Ads)"}`;
            } else if (resultData?.series?.title || resultData?.series?.name) {
                orderDescription = `Series: ${resultData.series.title || resultData.series.name}${isAdFree ? " (Ad-Free)" : " (With Ads)"}`;
            } else if (movieId) {
                orderDescription = `Movie Rental #${movieId}${isAdFree ? " (Ad-Free)" : " (With Ads)"}`;
            } else if (seriesId) {
                orderDescription = `Series Rental #${seriesId}${isAdFree ? " (Ad-Free)" : " (With Ads)"}`;
            }

            // 3. Configure and open Razorpay Checkout
            const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_SbNPoXlqIjm8Uv";

            const options = {
                key: razorpayKey,
                amount: Math.round(payableAmount * 100), // Amount in paise
                currency: "INR",
                name: resultData?.ott?.name || "Florese Store",
                description: orderDescription,
                prefill: {
                    name: resultData?.customer?.name || "",
                    email: resultData?.customer?.email || "",
                    contact: resultData?.customer?.mobileNumber || "",
                },
                theme: {
                    color: "#0d6efd",
                },
                handler: async function (razorpayResponse) {
                    const paymentId = razorpayResponse.razorpay_payment_id;
                    console.log("Transaction ID (razorpay_payment_id):", paymentId);
                    console.log("Razorpay Response:", razorpayResponse);

                    // Call Razorpay Verify API on payment success
                    try {
                        const verifyPayload = {
                            paymentId: paymentId,
                            userId: Number(userId),
                            ottId: Number(ottId),
                        };

                        if (planId) {
                            verifyPayload.planId = Number(planId);
                        }
                        if (movieId) {
                            verifyPayload.movieId = Number(movieId);
                            verifyPayload.isAdFree = isAdFree;
                        }
                        if (seriesId) {
                            verifyPayload.seriesId = Number(seriesId);
                            verifyPayload.isAdFree = isAdFree;
                        }
                        if (couponCode) {
                            verifyPayload.couponCode = couponCode;
                        }

                        console.log("Calling Razorpay Verify API with payload:", verifyPayload);

                        const verifyResponse = await axios.post(
                            `${baseURL}/api/v1/payment/razorpay/verify`,
                            verifyPayload,
                            {
                                headers: {
                                    'accept': '*/*',
                                    'Content-Type': 'application/json'
                                }
                            }
                        );

                        console.log("Razorpay Verify API Response:", verifyResponse.data);
                    } catch (verifyErr) {
                        console.error("Error verifying Razorpay payment on server:", verifyErr);
                    }

                    setPaymentSuccessData({
                        transactionId: paymentId,
                        orderId: razorpayResponse.razorpay_order_id,
                        signature: razorpayResponse.razorpay_signature,
                        amount: payableAmount,
                        actualPrice: resultData?.actualPrice,
                        discountedPrice: resultData?.discountedPrice,
                        couponApplied: resultData?.couponApplied,
                        couponDiscount: resultData?.couponDiscount,
                        coupon: resultData?.coupon,
                        plan: resultData?.plan,
                        movie: resultData?.movie,
                        series: resultData?.series,
                        isAdFree: isAdFree,
                        planId: planId,
                        movieId: movieId,
                        seriesId: seriesId,
                        ott: resultData?.ott,
                        customer: resultData?.customer
                    });

                    setLoading(false);
                },
                modal: {
                    ondismiss: function () {
                        console.log("Razorpay checkout closed by user.");
                        setLoading(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);

            rzp.on('payment.failed', function (failResponse) {
                console.error("Razorpay Payment Failed:", failResponse.error);
                setError(failResponse.error?.description || "Payment failed. Please try again.");
                setLoading(false);
            });

            rzp.open();

        } catch (err) {
            console.error("Error initiating Razorpay payment:", err);
            const message = err?.response?.data?.message || err.message || "An unexpected error occurred.";
            setError(message);
            setLoading(false);
        }
    }, [handleContinue]);

    useEffect(() => {
        if (hasCalled.current) return;
        hasCalled.current = true;

        initiatePayment();
    }, [initiatePayment]);

    const handleCopyTrx = () => {
        if (paymentSuccessData?.transactionId) {
            navigator.clipboard.writeText(paymentSuccessData.transactionId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                {paymentSuccessData ? (
                    <div>
                        <div style={styles.successBadge}>✓</div>
                        <h2 style={styles.title}>Payment Successful!</h2>
                        <p style={styles.subtitle}>Your transaction has been processed successfully.</p>

                        <div style={styles.infoBox}>
                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Transaction ID:</span>
                                <div style={styles.trxContainer}>
                                    <span style={styles.trxValue}>{paymentSuccessData.transactionId}</span>
                                    <button onClick={handleCopyTrx} style={styles.copyBtn}>
                                        {copied ? "Copied!" : "Copy"}
                                    </button>
                                </div>
                            </div>

                            {/* Coupon Code (if applicable) */}
                            {(paymentSuccessData.coupon?.couponCode || paymentSuccessData.couponApplied) && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>Coupon Code:</span>
                                    <div style={styles.couponContainer}>
                                        <span style={styles.couponBadge}>
                                            🏷️ {paymentSuccessData.coupon?.couponCode || "APPLIED"}
                                        </span>
                                        {paymentSuccessData.couponDiscount ? (
                                            <span style={styles.couponDiscountText}>
                                                (-₹{paymentSuccessData.couponDiscount})
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            <div style={styles.infoRow}>
                                <span style={styles.infoLabel}>Amount Paid:</span>
                                <span style={styles.infoValue}>₹{paymentSuccessData.amount}</span>
                            </div>
                            {paymentSuccessData.plan?.planName && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>Plan:</span>
                                    <span style={styles.infoValue}>{paymentSuccessData.plan.planName}</span>
                                </div>
                            )}
                            {(paymentSuccessData.movie || paymentSuccessData.movieId) && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>Movie Rental:</span>
                                    <span style={styles.infoValue}>
                                        {paymentSuccessData.movie?.title || paymentSuccessData.movie?.name || `Movie #${paymentSuccessData.movieId}`}
                                        {paymentSuccessData.isAdFree ? " (Ad-Free)" : " (With Ads)"}
                                    </span>
                                </div>
                            )}
                            {(paymentSuccessData.series || paymentSuccessData.seriesId) && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>Series Rental:</span>
                                    <span style={styles.infoValue}>
                                        {paymentSuccessData.series?.title || paymentSuccessData.series?.name || `Series #${paymentSuccessData.seriesId}`}
                                        {paymentSuccessData.isAdFree ? " (Ad-Free)" : " (With Ads)"}
                                    </span>
                                </div>
                            )}
                            {paymentSuccessData.ott?.name && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>OTT:</span>
                                    <span style={styles.infoValue}>{paymentSuccessData.ott.name}</span>
                                </div>
                            )}
                            {paymentSuccessData.customer?.name && (
                                <div style={styles.infoRow}>
                                    <span style={styles.infoLabel}>Customer:</span>
                                    <span style={styles.infoValue}>{paymentSuccessData.customer.name}</span>
                                </div>
                            )}
                        </div>

                        {/* Redirect Countdown Text */}
                        <p style={styles.redirectText}>
                            Redirecting to app in <span style={styles.countdownBadge}>{countdown}</span> {countdown === 1 ? 'second' : 'seconds'}...
                        </p>

                        <button
                            onClick={() => handleContinue(paymentSuccessData.transactionId)}
                            style={styles.primaryBtn}
                        >
                            Return to App
                        </button>
                    </div>
                ) : error ? (
                    <div>
                        <div style={styles.errorBadge}>✕</div>
                        <h2 style={styles.title}>Payment Failed / Error</h2>
                        <p style={styles.errorText}>{error}</p>

                        <div style={styles.btnGroup}>
                            <button onClick={initiatePayment} style={styles.primaryBtn}>
                                Retry Payment
                            </button>
                            <button
                                onClick={() => window.location.href = `multiott://payment-failed?error=${encodeURIComponent(error)}`}
                                style={styles.secondaryBtn}
                            >
                                Back to App
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={styles.spinner}></div>
                        <h2 style={styles.title}>
                            {loading ? "Processing Payment..." : "Multi OTT Razorpay"}
                        </h2>
                        <p style={styles.subtitle}>
                            {calculationData
                                ? `Payable Amount: ₹${calculationData.amountToBePaid}. Opening Razorpay...`
                                : "Calculating payable amount and preparing Razorpay gateway..."}
                        </p>

                        {!loading && (
                            <button onClick={initiatePayment} style={styles.primaryBtn}>
                                Pay Now
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f4f6f9",
        padding: "20px",
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    },
    card: {
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
        padding: "36px 28px",
        maxWidth: "460px",
        width: "100%",
        textAlign: "center",
    },
    title: {
        fontSize: "22px",
        fontWeight: "700",
        color: "#1e293b",
        margin: "12px 0 8px",
    },
    subtitle: {
        fontSize: "14px",
        color: "#64748b",
        marginBottom: "24px",
        lineHeight: "1.5",
    },
    spinner: {
        width: "48px",
        height: "48px",
        border: "4px solid #e2e8f0",
        borderTop: "4px solid #0d6efd",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
        margin: "16px auto 20px",
    },
    successBadge: {
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        backgroundColor: "#d1fae5",
        color: "#059669",
        fontSize: "28px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px",
    },
    errorBadge: {
        width: "60px",
        height: "60px",
        borderRadius: "50%",
        backgroundColor: "#fee2e2",
        color: "#dc2626",
        fontSize: "28px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px",
    },
    errorText: {
        color: "#dc2626",
        fontSize: "14px",
        backgroundColor: "#fef2f2",
        padding: "12px",
        borderRadius: "8px",
        marginBottom: "20px",
    },
    infoBox: {
        backgroundColor: "#f8fafc",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "24px",
        textAlign: "left",
        border: "1px solid #e2e8f0",
    },
    infoRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid #f1f5f9",
        fontSize: "14px",
    },
    infoLabel: {
        color: "#64748b",
        fontWeight: "500",
    },
    infoValue: {
        color: "#0f172a",
        fontWeight: "600",
    },
    trxContainer: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    trxValue: {
        fontFamily: "monospace",
        fontSize: "13px",
        color: "#0d6efd",
        fontWeight: "600",
    },
    couponContainer: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    couponBadge: {
        backgroundColor: "#ecfdf5",
        color: "#047857",
        border: "1px dashed #10b981",
        borderRadius: "6px",
        padding: "3px 8px",
        fontSize: "13px",
        fontWeight: "700",
        letterSpacing: "0.5px",
    },
    couponDiscountText: {
        fontSize: "12px",
        color: "#059669",
        fontWeight: "600",
    },
    copyBtn: {
        backgroundColor: "#e0f2fe",
        color: "#0284c7",
        border: "none",
        borderRadius: "6px",
        padding: "4px 8px",
        fontSize: "12px",
        cursor: "pointer",
        fontWeight: "600",
    },
    primaryBtn: {
        backgroundColor: "#0d6efd",
        color: "#ffffff",
        border: "none",
        borderRadius: "8px",
        padding: "12px 24px",
        fontSize: "15px",
        fontWeight: "600",
        cursor: "pointer",
        width: "100%",
        transition: "background-color 0.2s",
    },
    secondaryBtn: {
        backgroundColor: "#f1f5f9",
        color: "#475569",
        border: "none",
        borderRadius: "8px",
        padding: "12px 24px",
        fontSize: "15px",
        fontWeight: "600",
        cursor: "pointer",
        width: "100%",
        marginTop: "10px",
    },
    btnGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    redirectText: {
        marginTop: "16px",
        marginBottom: "16px",
        fontSize: "14px",
        color: "#64748b",
        fontWeight: "500",
    },
    countdownBadge: {
        color: "#0d6efd",
        fontWeight: "700",
        fontSize: "16px",
    }
};
